import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AccountStatus, Box, CardTypesEnum, Enrollment, Inscription } from '@common-fe/common-fe';

import { PERMISSIONS } from '@/common';
import { NoteTypes } from '@/common/constants.ts';
import { SelfBlockWrapper } from '@/components/wrappers/BlockWrapper';
import { useHasAccess } from '@/modules/core/hooks';
import useGetNotesQuery from '@/modules/employee/Employee/queries/useGetNotes.query';

import EnrollmentsAccountHistory from '../../Employee/EnrollmentBalances/components/EnrollmentsAccountHistory';
import { useGetAccountNumberQuery } from '../queries/useGetAccountNumber.query';

import { useCombineHSATrigger } from './CombineHsa/hooks/useCombineHSATrigger';
import { CombineHSAModal } from './CombineHsa';
import { EnrollmentDetailsAccountNumber } from './EnrollmentDetailsAccountNumber';
import EnrollmentDetailsContributions from './EnrollmentDetailsContributions';
import { EnrollmentDetailsEnrollmentStatus } from './EnrollmentDetailsEnrollmentStatus';
import { EnrollmentDetailsEnrollmentStatusHistory } from './EnrollmentDetailsEnrollmentStatusHistory';
import EnrollmentInvestments from './EnrollmentInvestments';

const REQUIRED_PERMISSIONS = [
  { permission: PERMISSIONS.VIEW_PLAN },
  { permission: PERMISSIONS.VIEW_PROFILE },
];

interface Props {
  id: string;
  employeeId: string;
  firstName?: string;
  lastName?: string;
  currentMyAccount: Enrollment;
  autoOpenCombineHSA?: boolean;
  onCombineHSAAutoOpenHandled?: () => void;
}

export const EnrollmentDetailsAccountInfo: React.FC<Props> = ({
  id,
  employeeId,
  firstName,
  lastName,
  currentMyAccount,
  autoOpenCombineHSA,
  onCombineHSAAutoOpenHandled,
}) => {
  const [isCombineHSAModalVisible, setIsCombineHSAModalVisible] = useState(false);
  const coveragePeriods = useMemo(() => currentMyAccount?.coveragePeriods, [currentMyAccount]);
  const isHSA = useMemo(() => currentMyAccount?.accountType === CardTypesEnum.HSA, [currentMyAccount]);
  const isAccountClosed = useMemo(
    () => currentMyAccount?.accountStatus === AccountStatus.CLOSED,
    [currentMyAccount?.accountStatus]
  );
  const hasAccess = useHasAccess(REQUIRED_PERMISSIONS);

  const isHasProfilePermission = useHasAccess([{ permission: PERMISSIONS.VIEW_HSA_DETAILS }]);

  const { formattedData } = useGetAccountNumberQuery(id, isHSA);
  const { notes } = useGetNotesQuery(NoteTypes.PLAN_NOTES, currentMyAccount?.planId);

  const accountNumber = useMemo(
    () => (formattedData.accountNumber ? formattedData.accountNumber : null),
    [formattedData]
  );

  const { isCombineHSATriggerVisible, isCombineHSATriggerLoading } = useCombineHSATrigger({
    accountNumber: accountNumber ?? '',
    employeeId,
    isHSA,
    firstName,
    lastName,
  });

  const routingNumber = useMemo(
    () => (formattedData.routingNumber ? formattedData.routingNumber : null),
    [formattedData]
  );

  const electionPeriods = useMemo(() => currentMyAccount?.electionPeriods, [currentMyAccount]);

  const isMoreThanOneCoveragePeriod = useMemo(
    () => coveragePeriods && coveragePeriods.length > 1,
    [coveragePeriods]
  );

  const handleCombineHSAClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setIsCombineHSAModalVisible(true);
  }, []);

  useEffect(() => {
    if (autoOpenCombineHSA && !isCombineHSATriggerLoading && isCombineHSATriggerVisible) {
      setIsCombineHSAModalVisible(true);
      onCombineHSAAutoOpenHandled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCombineHSA, isCombineHSATriggerLoading, isCombineHSATriggerVisible]);

  const handleSetCombineHSAModalVisible = useCallback(
    (value: boolean) => setIsCombineHSAModalVisible(value),
    []
  );

  return (
    <Box gap="spacing24">
      {isHSA && isHasProfilePermission && (
        <Box gap="spacing24">
          <SelfBlockWrapper
            moduleAnchorId="account_number"
            nodeTitle={
              <Box direction="row" align="center" justify="between" style={{ flex: 1 }}>
                <Inscription size="large" margin={{ left: 'spacing8' }} color="textTitle" weight={700}>
                  Account number
                </Inscription>
                {isCombineHSATriggerVisible && (
                  <Inscription
                    color="textAccent"
                    weight="normal"
                    data-testid="combine-hsa-trigger"
                    onClick={handleCombineHSAClick}
                  >
                    Combine HSA
                  </Inscription>
                )}
              </Box>
            }
          >
            <EnrollmentDetailsAccountNumber accountNumber={accountNumber} routingNumber={routingNumber} />
          </SelfBlockWrapper>
          {(currentMyAccount?.allowIndividualContributions || currentMyAccount?.allowInvestments) &&
            isHasProfilePermission && (
              <SelfBlockWrapper gap="spacing16" title="Funds management" moduleAnchorId="investments_status">
                {currentMyAccount?.allowInvestments && (
                  <EnrollmentInvestments employeeId={employeeId} isAccountClosed={isAccountClosed} />
                )}
                {currentMyAccount?.allowIndividualContributions && (
                  <EnrollmentDetailsContributions employeeId={employeeId} isAccountClosed={isAccountClosed} />
                )}
              </SelfBlockWrapper>
            )}
        </Box>
      )}
      {currentMyAccount && (
        <SelfBlockWrapper title="Enrollment status" moduleAnchorId="enrollment_status">
          <EnrollmentDetailsEnrollmentStatus
            coverageDateEnd={currentMyAccount.coverageDateEnd}
            coverageDateStart={currentMyAccount.coverageDateStart}
            coveragePeriods={currentMyAccount?.coveragePeriods}
            submitClaimsDateEnd={currentMyAccount.submitClaimsDateEnd}
            enrollmentStatus={currentMyAccount.enrollmentStatus}
            limitedPeriods={
              isMoreThanOneCoveragePeriod ? undefined : currentMyAccount?.coveragePeriods?.[0]?.limitedPeriods
            }
            externalDeductibleMet={
              isMoreThanOneCoveragePeriod ? undefined : currentMyAccount.deductibleMetDate
            }
            isRollover={currentMyAccount?.isRollover}
            notes={notes}
            hasAccessToNotes={hasAccess}
            policyId={currentMyAccount.policyId}
            accountName={currentMyAccount.name}
            accountType={currentMyAccount.accountType}
          />
          {(isMoreThanOneCoveragePeriod || electionPeriods?.length) && (
            <Box margin={{ top: 'spacing16' }}>
              <EnrollmentDetailsEnrollmentStatusHistory
                coveragePeriods={currentMyAccount?.coveragePeriods}
                policyId={currentMyAccount.policyId}
                externalDeductibleMet={currentMyAccount.deductibleMetDate}
                electionPeriods={electionPeriods}
              />
            </Box>
          )}
        </SelfBlockWrapper>
      )}
      <EnrollmentsAccountHistory accountId={id} />
      <CombineHSAModal
        accountNumber={accountNumber ?? ''}
        employeeId={employeeId}
        firstName={firstName}
        lastName={lastName}
        visible={isCombineHSAModalVisible}
        onSetVisible={handleSetCombineHSAModalVisible}
      />
    </Box>
  );
};
